import React from 'react';
import { XCircleIcon } from './Icons';

interface HelpModalProps {
  onClose: () => void;
}

const HelpSection: React.FC<{ title: string, children: React.ReactNode }> = ({ title, children }) => (
    <div className="mb-6">
        <h3 className="text-xl font-bold text-blue-400 border-b border-slate-700 pb-2 mb-3">{title}</h3>
        <div className="space-y-3 text-slate-300 text-sm leading-relaxed">
            {children}
        </div>
    </div>
);

const HelpModal: React.FC<HelpModalProps> = ({ onClose }) => {
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-slide-up-fade-in no-print" role="dialog" aria-modal="true">
      <div className="bg-slate-800 rounded-lg shadow-2xl w-full max-w-3xl h-full max-h-[90vh] flex flex-col border border-slate-700">
        <header className="flex-shrink-0 flex items-center justify-between p-4 border-b border-slate-700">
          <h2 className="text-2xl font-bold text-white">App Guide & Help</h2>
          <button onClick={onClose} className="text-slate-500 hover:text-white transition-colors">
            <XCircleIcon className="w-8 h-8" />
            <span className="sr-only">Close help</span>
          </button>
        </header>
        
        <main className="flex-grow p-6 overflow-y-auto">
          <HelpSection title="General Navigation & Icons">
            <p>This app helps you track flight operations in real-time. Use the tabs at the top to switch between different screens.</p>
            <ul className="list-disc list-inside space-y-2 pl-2">
                <li><strong className="text-white">Header Icons:</strong> Quickly access Help, Mute/Unmute audio alerts, start a New Session (clears current data after saving it as a report), and Logout.</li>
                <li><strong className="text-white">News Bar:</strong> The scrolling red bar at the top displays important announcements and key milestones for all users to see.</li>
                <li><strong className="text-white">Footer:</strong> Shows the current time, a countdown to the ETD (Estimated Time of Departure), and the status of data synchronization with the server (<span className="text-green-400">SEND</span> / <span className="text-red-400">RECV</span>).</li>
            </ul>
          </HelpSection>

          <HelpSection title="Overview Screen">
            <p>This screen provides a high-level summary of the entire flight operation.</p>
            <ul className="list-disc list-inside space-y-2 pl-2">
                <li><strong className="text-white">Progress Bar:</strong> Visualizes the flight's progress through all stations. Completed stations are blue, the current station pulses with a glow, and upcoming stations are grey. During boarding, relevant stations turn green.</li>
                <li><strong className="text-white">Immigration Status:</strong> Shows whether immigration has been approved for the flight.</li>
                <li><strong className="text-white">Key Figures:</strong> Displays booking numbers, ETD, and important milestones like Crew Arrival and Doors Closed times.</li>
            </ul>
          </HelpSection>
          
          <HelpSection title="Crew Movement Screen">
            <p>This is the main screen for logging operational timestamps.</p>
            <ul className="list-disc list-inside space-y-2 pl-2">
                <li><strong className="text-white">Logging Time:</strong> Tap the <strong className="text-blue-400">Crew</strong> or <strong className="text-amber-400">Pilot</strong> icon for a specific station to record the current time. The icon will turn blue or amber, respectively, and display the recorded time below it. Tapping again will overwrite the previous time.</li>
                <li><strong className="text-white">Paired Arrivals:</strong> Use the "Crew & Pilots Arrived Together" checkbox if both groups are moving together. When checked, tapping one icon will automatically log the same time for the other.</li>
                <li><strong className="text-white">Flight Setup:</strong> Select the Flight Number and Date at the top of the screen to start a new report.</li>
                <li><strong className="text-white">Capture Document:</strong> Use your device's camera to take a picture of a document and attach it to the report.</li>
            </ul>
          </HelpSection>

          <HelpSection title="Counter Distribution Screen">
            <p>Manage passenger figures and staff assignments for check-in counters and gates.</p>
            <ul className="list-disc list-inside space-y-2 pl-2">
                <li><strong className="text-white">Editable Fields:</strong> All fields on this screen are editable. Simply tap and type to update booking figures, staff names at counters, or gate assignments. Changes are saved automatically and are visible to all other users in real-time.</li>
                <li><strong className="text-white">Auto-Calculation:</strong> The "Total Passengers" fields are calculated automatically based on the C, M, and Inf (Infant) numbers you enter.</li>
            </ul>
          </HelpSection>
          
          <HelpSection title="Security Screen">
            <p>Record passengers who have been selected for the security scan (ETD Sheet).</p>
             <ul className="list-disc list-inside space-y-2 pl-2">
                <li><strong className="text-white">Scan Target:</strong> The app calculates the target number of scans based on a percentage (set by Admins) of the total passengers.</li>
                <li><strong className="text-white">Scan Boarding Pass:</strong> Opens your device's camera to scan the PDF417 barcode on a boarding pass. It will automatically extract the passenger's name and seat number.</li>
                <li><strong className="text-white">Add Manually:</strong> If a scan fails or is not possible, use this option to type in the passenger's details.</li>
                <li><strong className="text-white">Finalize Report:</strong> Once all security checks are complete and the flight is ready, use this button to save the current flight as a final report and start a new, empty session.</li>
            </ul>
          </HelpSection>

          <HelpSection title="Reports Screen">
            <p>View, export, and manage all past flight reports.</p>
             <ul className="list-disc list-inside space-y-2 pl-2">
                <li><strong className="text-white">Select a Report:</strong> Choose a flight from the list on the left to view its detailed report.</li>
                <li><strong className="text-white">Export & Share:</strong> Use the icons at the top to Delete (Admins/Supervisors only), export as a <strong className="text-green-400">PDF</strong>, send via <strong className="text-sky-400">Email</strong>, or <strong className="text-blue-400">Print</strong> the report.</li>
            </ul>
          </HelpSection>
          
           <HelpSection title="Admin Panel (Admins Only)">
            <p>Manage users, system settings, and application assets.</p>
             <ul className="list-disc list-inside space-y-2 pl-2">
                <li><strong className="text-white">User Management:</strong> Create, delete, and manage user roles and passwords.</li>
                <li><strong className="text-white">System Settings:</strong> Control app-wide settings like registration status, authorization codes for new users, and the list of available flight numbers.</li>
                <li><strong className="text-white">App Dependencies:</strong> Customize the app by uploading your own logos and icons.</li>
                <li><strong className="text-white">History Logs:</strong> View detailed logs of user logins and when new flight sessions were started.</li>
             </ul>
          </HelpSection>

        </main>
        
        <footer className="flex-shrink-0 flex items-center justify-end p-4 border-t border-slate-700">
            <button
              onClick={() => {
                localStorage.removeItem('hasCompletedTour');
                window.location.reload();
              }}
              className="bg-amber-600 hover:bg-amber-700 text-white font-bold py-2 px-6 rounded-lg transition-colors mr-4"
            >
              Restart Guided Tour
            </button>
            <button onClick={onClose} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-lg transition-colors">
              Close
            </button>
        </footer>
      </div>
    </div>
  );
};

export default HelpModal;

const BrowserInstruction: React.FC<{ browserName: string; children: React.ReactNode }> = ({ browserName, children }) => (
    <div>
        <h4 className="text-lg font-semibold text-white mb-2">{browserName}</h4>
        <ol className="list-decimal list-inside space-y-2 text-slate-300">
            {children}
        </ol>
    </div>
);

export const NotificationHelpModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-slide-up-fade-in no-print" role="dialog" aria-modal="true">
            <div className="bg-slate-800 rounded-lg shadow-2xl w-full max-w-2xl h-full max-h-[90vh] flex flex-col border border-slate-700">
                <header className="flex-shrink-0 flex items-center justify-between p-4 border-b border-slate-700">
                    <h2 className="text-2xl font-bold text-white">How to Enable Notifications</h2>
                    <button onClick={onClose} className="text-slate-500 hover:text-white transition-colors">
                        <XCircleIcon className="w-8 h-8" />
                        <span className="sr-only">Close help</span>
                    </button>
                </header>
                <main className="flex-grow p-6 overflow-y-auto">
                    <p className="mb-6 text-slate-300">
                        It looks like notifications are blocked in your browser. To receive real-time push alerts, please follow the instructions for your browser below and then reload the page.
                    </p>
                    <div className="space-y-6">
                        <BrowserInstruction browserName="Google Chrome (Desktop)">
                            <li>Click the <strong>lock icon (🔒)</strong> to the left of the website address in the address bar.</li>
                            <li>Find the "Notifications" setting and change it from "Block" to <strong>"Allow"</strong>.</li>
                            <li>Reload the page for the changes to take effect.</li>
                        </BrowserInstruction>
                        <BrowserInstruction browserName="Mozilla Firefox (Desktop)">
                            <li>Click the <strong>lock icon (🔒)</strong> in the address bar.</li>
                            <li>Click the arrow <strong>(&gt;)</strong> in the Site Information panel.</li>
                            <li>Click "More Information", then go to the <strong>"Permissions"</strong> tab.</li>
                            <li>Find "Send Notifications" and uncheck "Use Default", then select <strong>"Allow"</strong>.</li>
                        </BrowserInstruction>
                        <BrowserInstruction browserName="Safari (macOS)">
                            <li>With the website open, go to <strong>Safari &gt; Settings</strong> from the menu bar.</li>
                            <li>Click on the <strong>"Websites"</strong> tab, then select <strong>"Notifications"</strong> from the side panel.</li>
                            <li>Find this website in the list and change its permission to <strong>"Allow"</strong>.</li>
                        </BrowserInstruction>
                         <BrowserInstruction browserName="Microsoft Edge">
                            <li>Click the <strong>lock icon (🔒)</strong> in the address bar.</li>
                            <li>In the dropdown, find "Notifications" and select <strong>"Allow"</strong> from the menu next to it.</li>
                            <li>You may need to click "Site permissions" if the option isn't immediately visible.</li>
                        </BrowserInstruction>
                         <BrowserInstruction browserName="Android (Chrome)">
                            <li>Tap the <strong>three dots (⋮)</strong> in the top-right corner and go to <strong>"Settings"</strong>.</li>
                            <li>Tap on <strong>"Site settings"</strong> &gt; <strong>"Notifications"</strong>.</li>
                            <li>Find this website under the "Blocked" section, tap on it, and change the permission to <strong>"Allow"</strong>.</li>
                        </BrowserInstruction>
                    </div>
                </main>
                <footer className="flex-shrink-0 flex items-center justify-end p-4 border-t border-slate-700">
                    <button onClick={onClose} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-lg transition-colors">
                        Got it, Close
                    </button>
                </footer>
            </div>
        </div>
    );
};